// path: frontend/src/components/pages/chat/ChatHeader.jsx
import React, { useMemo, useEffect, useRef } from "react";
import {
  PanelLeft,
  PanelLeftDashed,
  Sun,
  Moon,
  Settings2,
  X,
} from "lucide-react";
import { Usage } from "./usage";
import { PersonasDropdown } from "./personas";
import ModelDropdown from "./ModelDropdown";
import ImageModelsDropdown from "./ImageModelsDropdown";
import ResearchModelsDropdown from "./ResearchModelsDropdown";

const ChatHeader = ({
  sidebarExpanded,
  toggleSidebar,
  rightPanelExpanded,
  toggleRightPanel,
  theme,
  toggleTheme,
  userData,
  hideUsage,
  selectedPersona,
  onPersonaSelect,
  // Model dropdown props
  models,
  selectedModel,
  onModelSelect,
  activeTool,
  isUsingFallbackModel = false, // Track if fallback model is active
}) => {
  // Defensive mapping for models to ensure value, label, and support fields
  const mappedModels = useMemo(() => {
    // Ensure models is always an array
    const modelsArray = Array.isArray(models) ? models : [];

    return modelsArray.map((m) => ({
      ...m,
      value: m._id || m.value || m.name || "",
      label: m.name
        ? m.name
            .replace(/.*\//, "")
            .replace(/[-_]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        : m.label || "Unknown",
      support: m.support || {},
    }));
  }, [models]);

  // Use all models - filtering is now handled in ModelDropdown
  const filteredModels = mappedModels;

  // Try to find the Nano Banana model by name (more reliable than hardcoded ID)
  // Check for both production and preview versions
  const nanoBananaModel = useMemo(() => {
    return filteredModels.find(
      (m) =>
        m.name === "google/gemini-2.5-flash-image" ||
        m.name === "google/gemini-2.5-flash-image-preview" ||
        (m.name &&
          m.name.includes("gemini") &&
          m.name.includes("image") &&
          m.name.includes("2.5"))
    );
  }, [filteredModels]);

  // Check if Nano Banana is active by comparing selectedModel with the found model's ID/value
  // Also check by name as fallback
  const isNanoBananaActive = useMemo(() => {
    if (!selectedModel || !nanoBananaModel) return false;

    // Compare by ID/value
    const isActiveById =
      selectedModel === nanoBananaModel._id ||
      selectedModel === nanoBananaModel.value ||
      String(selectedModel) === String(nanoBananaModel._id) ||
      String(selectedModel) === String(nanoBananaModel.value);

    // Also check if selected model has the same name (fallback)
    const selectedModelData = filteredModels.find(
      (m) => m._id === selectedModel || m.value === selectedModel
    );
    const isActiveByName =
      selectedModelData &&
      (selectedModelData.name === "google/gemini-2.5-flash-image" ||
        selectedModelData.name === "google/gemini-2.5-flash-image-preview" ||
        (selectedModelData.name &&
          selectedModelData.name.includes("gemini") &&
          selectedModelData.name.includes("image") &&
          selectedModelData.name.includes("2.5")));

    return isActiveById || isActiveByName;
  }, [selectedModel, nanoBananaModel, filteredModels]);

  // Find auto model (isAuto flag or name contains 'auto')
  const autoModel =
    filteredModels.find((m) => m.isAuto) ||
    filteredModels.find((m) => (m.name || "").toLowerCase().includes("auto"));

  // Ensure a model is always selected (but don't interfere when image or research tool is active)
  // Only auto-select when selectedModel is null/undefined, not when it's empty string (which means "Default/Auto" was explicitly selected)
  useEffect(() => {
    // Skip auto-selection if image or research tool is active - let respective dropdowns handle it
    if (activeTool === "image" || activeTool === "research") return;

    // Ensure filteredModels is an array and has items
    if (Array.isArray(filteredModels) && filteredModels.length > 0) {
      // Only auto-select if selectedModel is null or undefined (not set yet)
      // Don't auto-select if selectedModel is "" (empty string), as that means user explicitly selected "Default/Auto"
      if (selectedModel === null || selectedModel === undefined) {
        if (autoModel) {
          // Select empty string to trigger backend's auto model selection
          // This ensures backend uses the model marked as isAuto: true
          onModelSelect("");
          return;
        }
        const preferredId = "6867bb36a2c0a4a362937bbf";
        const preferredModel = filteredModels.find(
          (m) => m && (m.value === preferredId || m._id === preferredId)
        );
        if (preferredModel) {
          onModelSelect(preferredModel._id || preferredModel.value);
        } else if (filteredModels[0]) {
          onModelSelect(filteredModels[0]._id || filteredModels[0].value);
        }
      }
    }
  }, [filteredModels, selectedModel, onModelSelect, activeTool, autoModel]);

  // Track previous activeTool to detect transitions
  const prevActiveToolRef = useRef(activeTool);

  // Revert to default model when image or research tool is disabled
  useEffect(() => {
    const prevActiveTool = prevActiveToolRef.current;
    const currentActiveTool = activeTool;

    // Update ref for next render
    prevActiveToolRef.current = activeTool;

    // Only trigger when switching FROM image or research tool to no tool (or different tool)
    const wasImageOrResearch =
      prevActiveTool === "image" || prevActiveTool === "research";
    const isNowNotImageOrResearch =
      currentActiveTool !== "image" && currentActiveTool !== "research";

    if (
      wasImageOrResearch &&
      isNowNotImageOrResearch &&
      Array.isArray(filteredModels) &&
      filteredModels.length > 0 &&
      selectedModel
    ) {
      const currentModel = filteredModels.find(
        (m) => m && (m.value === selectedModel || m._id === selectedModel)
      );

      if (!currentModel) return;

      // Check if current model is an image model, research model, or NanoBanana
      const isImageModel =
        currentModel.modelType === "image" ||
        currentModel.support?.imageOut === true ||
        (nanoBananaModel &&
          (currentModel._id === nanoBananaModel._id ||
            currentModel.value === nanoBananaModel.value)) ||
        currentModel.name === "google/gemini-2.5-flash-image" ||
        currentModel.name === "google/gemini-2.5-flash-image-preview" ||
        (currentModel.name &&
          currentModel.name.includes("gemini") &&
          currentModel.name.includes("image"));

      const isResearchModel = currentModel.provider === "Perplexity";

      // If current model is an image model (including NanoBanana) or research model, switch to default
      if (isImageModel || isResearchModel) {
        // Prefer auto model first, then preferred default model
        if (
          autoModel &&
          autoModel._id !== selectedModel &&
          autoModel.value !== selectedModel
        ) {
          onModelSelect(autoModel._id || autoModel.value);
          return;
        }

        const preferredId = "6867bb36a2c0a4a362937bbf";
        const preferredModel = filteredModels.find(
          (m) => m && (m.value === preferredId || m._id === preferredId)
        );
        if (
          preferredModel &&
          preferredModel._id !== selectedModel &&
          preferredModel.value !== selectedModel
        ) {
          onModelSelect(preferredModel._id || preferredModel.value);
        } else {
          // Fallback to first non-image, non-research model
          const firstNonSpecialModel = filteredModels.find(
            (m) =>
              m &&
              m.modelType !== "image" &&
              m.provider !== "Perplexity" &&
              m._id !== nanoBananaModel?._id &&
              m.value !== nanoBananaModel?.value &&
              m.name !== "google/gemini-2.5-flash-image" &&
              m.name !== "google/gemini-2.5-flash-image-preview" &&
              !(m.support?.imageOut === true) &&
              m._id !== selectedModel &&
              m.value !== selectedModel
          );
          if (firstNonSpecialModel) {
            onModelSelect(
              firstNonSpecialModel._id || firstNonSpecialModel.value
            );
          }
        }
      }
    }
  }, [
    activeTool,
    filteredModels,
    selectedModel,
    onModelSelect,
    autoModel,
    nanoBananaModel,
  ]);

  // Handle Nano Banana selection
  const handleNanoBananaClick = () => {
    if (!Array.isArray(filteredModels) || filteredModels.length === 0) {
      return;
    }

    if (isNanoBananaActive) {
      // If already active, deselect and go back to auto model
      const preferredId = "6867bb36a2c0a4a362937bbf";
      const preferredModel = filteredModels.find(
        (m) => m && (m.value === preferredId || m._id === preferredId)
      );
      if (preferredModel) {
        onModelSelect(preferredModel._id || preferredModel.value);
      } else if (filteredModels[0]) {
        // Fallback to first available model
        onModelSelect(filteredModels[0]._id || filteredModels[0].value);
      }
    } else {
      // Select Nano Banana model (production model preferred)
      if (nanoBananaModel) {
        // Prefer production model over preview
        const productionModel = filteredModels.find(
          (m) => m.name === "google/gemini-2.5-flash-image"
        );
        const modelToUse = productionModel || nanoBananaModel;
        onModelSelect(modelToUse._id || modelToUse.value);
      } else {
        // Fallback: try to find by name if not found in initial lookup
        const nanoBananaModelToSelect = filteredModels.find(
          (m) =>
            m &&
            (m.name === "google/gemini-2.5-flash-image" ||
              m.name === "google/gemini-2.5-flash-image-preview" ||
              (m.name &&
                m.name.includes("gemini") &&
                m.name.includes("image") &&
                m.name.includes("2.5")))
        );
        if (nanoBananaModelToSelect) {
          onModelSelect(
            nanoBananaModelToSelect._id || nanoBananaModelToSelect.value
          );
        }
      }
    }
  };

  // Banana icon component with updated SVG
  const BananaIcon = ({ size = 20 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 26.4304C2 25.7004 2.592 25.1094 3.322 25.1094H4.312C4.65619 25.1188 4.98311 25.2622 5.22321 25.509C5.46331 25.7558 5.59764 26.0866 5.59764 26.4309C5.59764 26.7752 5.46331 27.1059 5.22321 27.3527C4.98311 27.5995 4.65619 27.7429 4.312 27.7524H3.322C2.592 27.7524 2 27.1604 2 26.4304Z"
        fill="#F3AD61"
      />

      <path
        d="M19.783 18.0003H29.218C29.913 18.0003 30.263 17.1243 29.768 16.6083L27.59 14.3363C27.1932 13.9147 26.7143 13.5787 26.1828 13.3491C25.6513 13.1195 25.0784 13.001 24.4995 13.001C23.9205 13.001 23.3476 13.1195 22.8161 13.3491C22.2847 13.5787 21.8058 13.9147 21.409 14.3363L19.23 16.6083C18.738 17.1223 19.088 18.0003 19.783 18.0003ZM4.78296 18.0003H14.217C14.912 18.0003 15.262 17.1223 14.77 16.6083L12.59 14.3363C12.1932 13.9144 11.7143 13.5782 11.1827 13.3484C10.6511 13.1186 10.0781 13 9.49896 13C8.91982 13 8.3468 13.1186 7.81521 13.3484C7.28362 13.5782 6.8047 13.9144 6.40796 14.3363L4.22996 16.6083C3.73796 17.1223 4.08796 18.0003 4.78296 18.0003Z"
        fill="#FFBA12"
      />

      <path
        d="M16.7283 2.22656C17.859 2.06385 18.9092 2.65891 19.8298 3.79395C20.7495 4.92784 21.5115 6.56962 22.0564 8.40527C22.6006 10.2386 22.9235 12.2521 22.9734 14.1211C23.0234 15.9935 22.7987 17.703 22.2615 18.9385C21.1758 21.4353 19.4637 22.959 17.3855 24.1084C15.2942 25.265 12.8563 26.0319 10.3015 27.0361C9.84647 27.2147 9.36598 27.3178 8.87866 27.3418L8.66968 27.3477C8.16117 27.3477 7.71394 27.4524 7.32397 27.5732C7.12995 27.6334 6.94697 27.6983 6.78101 27.7559C6.61275 27.8142 6.46215 27.865 6.323 27.9004C6.04282 27.9716 5.8458 27.9715 5.68237 27.8818C5.51425 27.7894 5.3344 27.5755 5.15796 27.1025C4.25277 24.6662 5.44161 21.9414 7.80347 21.0146C10.5597 19.9332 12.0169 17.8514 13.1013 15.084C13.6522 13.6792 13.9501 12.6784 14.1082 11.6416C14.2658 10.6078 14.283 9.5448 14.281 8.02246C14.2785 6.79053 14.2391 5.42509 14.5378 4.31348C14.686 3.76214 14.914 3.28791 15.2595 2.92969C15.6022 2.57441 16.0724 2.32085 16.7283 2.22656Z"
        fill="white"
        stroke="#B17D00"
        strokeWidth="0.4"
      />

      <path
        d="M22.445 19.0198L12.985 14.6758C11.899 17.1738 9.93198 19.0868 7.44498 20.0658C4.56398 21.1978 2.63798 24.2958 4.21498 27.4518C7.36598 33.7558 22.445 27.3858 22.445 19.0188"
        fill="#FFD122"
      />

      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.2401 16.481C18.4294 16.5416 18.5869 16.6748 18.678 16.8514C18.7691 17.0281 18.7864 17.2336 18.7261 17.423C16.7461 23.624 10.9701 26.488 5.8081 26.753C5.6101 26.7621 5.41656 26.6924 5.26976 26.5593C5.12296 26.4261 5.03485 26.2402 5.02467 26.0423C5.0145 25.8443 5.08309 25.6504 5.21546 25.5029C5.34783 25.3554 5.53321 25.2663 5.7311 25.255C10.4691 25.012 15.5611 22.403 17.2971 16.967C17.3271 16.8732 17.3753 16.7861 17.4389 16.7109C17.5025 16.6357 17.5803 16.5738 17.6679 16.5286C17.7555 16.4835 17.8511 16.4561 17.9493 16.4479C18.0474 16.4397 18.1463 16.451 18.2401 16.481Z"
        fill="#FFBB15"
      />

      <path
        d="M23.24 26.4675L13.043 14.6875H22.047C24.173 14.7945 25.841 16.5975 25.841 18.7845V26.3365C25.841 27.6665 24.643 28.0725 23.24 26.4675Z"
        fill="#FFF478"
      />
    </svg>
  );

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-5 py-2 md:py-3 gap-2 md:gap-0">
      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
        <button
          onClick={toggleSidebar}
          className="p-2 text-fg-60 hover:text-fg-50 transition-colors bg-bg-50 rounded-full touch-manipulation"
          aria-label="Toggle sidebar"
        >
          {sidebarExpanded ? (
            <PanelLeftDashed size={20} strokeWidth={1.5} />
          ) : (
            <PanelLeft size={20} strokeWidth={1.5} />
          )}
        </button>
        {!hideUsage && userData?._id && <Usage userId={userData._id} />}
        {/* Model Dropdown */}
        {!hideUsage && filteredModels && filteredModels.length > 0 && (
          <div className="w-auto min-w-[120px] md:min-w-[140px] flex-1 md:flex-none">
            {activeTool === "image" ? (
              <ImageModelsDropdown
                models={filteredModels}
                selectedModel={selectedModel}
                onSelect={onModelSelect}
                placeholder="Select image model"
              />
            ) : activeTool === "research" ? (
              <ResearchModelsDropdown
                models={filteredModels}
                selectedModel={selectedModel}
                onSelect={onModelSelect}
                placeholder="Select research model"
              />
            ) : (
              <ModelDropdown
                models={filteredModels}
                selectedModel={selectedModel}
                onSelect={onModelSelect}
                placeholder="Select a model"
                isUsingFallbackModel={isUsingFallbackModel}
              />
            )}
          </div>
        )}

        {/* Nano Banana Button */}
        {!hideUsage && filteredModels && filteredModels.length > 0 && (
          <button
            onClick={handleNanoBananaClick}
            className={`flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 relative touch-manipulation ${
              isNanoBananaActive
                ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-900"
                : "bg-bg-50 text-fg-60 hover:text-fg-50 hover:bg-bg-40 border border-transparent"
            }`}
            title="Nano Banana - Gemini 2.5 Flash Image"
          >
            <BananaIcon size={14} />
            <span className="hidden sm:inline">Nano Banana</span>

            {/* New Badge - only show when not active */}
            {!isNanoBananaActive && (
              <span className="absolute -top-1 -right-1 bg-yellow-600 dark:bg-yellow-900 text-white text-[9px] font-bold px-1 py-0.5 rounded-full leading-none">
                NEW
              </span>
            )}

            {/* Cross icon - only show when active */}
            {isNanoBananaActive && (
              <div className="ml-1 p-0.5 rounded-full transition-colors">
                <X size={10} strokeWidth={2} />
              </div>
            )}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end">
        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Personas Dropdown */}
          <PersonasDropdown
            onPersonaSelect={onPersonaSelect}
            selectedPersona={selectedPersona}
          />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full text-fg-60 hover:text-fg-50 transition-colors bg-bg-50 touch-manipulation"
            aria-label="Toggle theme"
          >
            {theme === "light" ? (
              <Moon size={18} strokeWidth={1.5} />
            ) : (
              <Sun size={18} strokeWidth={1.5} />
            )}
          </button>
          <button
            onClick={toggleRightPanel}
            className="p-2 rounded-full text-fg-60 hover:text-fg-50 transition-colors bg-bg-50 touch-manipulation"
            aria-label="Toggle right panel"
          >
            <Settings2 size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
